import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ORDER_STATUS } from '../../firebase/orders';
import { getErrorMessage } from '../../utils/errorUtils';
import './AdminOrderArchive.css';

function formatDate(value) {
    if (!value) return '—';
    const d = value?.toDate ? value.toDate() : new Date(value?.seconds ? value.seconds * 1000 : value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminOrderArchive() {
    const [records,       setRecords]       = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [deleteDialog,  setDeleteDialog]  = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError,   setActionError]   = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const q    = query(collection(db, 'archivedOrders'), orderBy('archivedAt', 'desc'));
                const snap = await getDocs(q);
                setRecords(snap.docs.map(d => ({ firestoreId: d.id, ...d.data() })));
            } catch (err) {
                setActionError(getErrorMessage(err, 'Failed to load archived orders.'));
            }
            setLoading(false);
        };
        load();
    }, []);

    // Separate into cancelled and delivered orders
    const cancelledOrders = records.filter(r => r.status === ORDER_STATUS.CANCELLED);
    const deliveredOrders = records.filter(r => r.status === ORDER_STATUS.DELIVERED);

    const handleDelete = async () => {
        if (!deleteDialog) return;
        setActionLoading(true);

        try {
            if (deleteDialog === 'all') {
                await Promise.all(records.map(r => deleteDoc(doc(db, 'archivedOrders', r.firestoreId))));
                setRecords([]);
            } else if (deleteDialog === 'cancelled') {
                await Promise.all(cancelledOrders.map(r => deleteDoc(doc(db, 'archivedOrders', r.firestoreId))));
                setRecords(prev => prev.filter(r => r.status !== ORDER_STATUS.CANCELLED));
            } else if (deleteDialog === 'delivered') {
                await Promise.all(deliveredOrders.map(r => deleteDoc(doc(db, 'archivedOrders', r.firestoreId))));
                setRecords(prev => prev.filter(r => r.status !== ORDER_STATUS.DELIVERED));
            } else {
                await deleteDoc(doc(db, 'archivedOrders', deleteDialog.firestoreId));
                setRecords(prev => prev.filter(r => r.firestoreId !== deleteDialog.firestoreId));
            }
        } catch (err) {
            setActionError(getErrorMessage(err, 'Failed to delete.'));
        }

        setActionLoading(false);
        setDeleteDialog(null);
    };

    if (loading) return <div className="apt-loading">Loading past transactions…</div>;

    return (
        <div className="apt-wrapper">
            {actionError && (
                <div className="apt-banner apt-banner--error" onClick={() => setActionError(null)}>
                    {actionError}
                </div>
            )}

            {/* Confirm dialog */}
            {deleteDialog && (
                <div className="apt-dialog-overlay" onClick={() => !actionLoading && setDeleteDialog(null)}>
                    <div className="apt-dialog" onClick={e => e.stopPropagation()}>
                        <h3 className="apt-dialog-title">
                            {deleteDialog === 'all' 
                                ? 'Delete All Records?' 
                                : deleteDialog === 'cancelled' 
                                ? 'Delete All Cancelled Orders?' 
                                : deleteDialog === 'delivered'
                                ? 'Delete All Delivered Orders?'
                                : 'Delete Record?'
                            }
                        </h3>
                        <p className="apt-dialog-message">
                            {deleteDialog === 'all'
                                ? 'This will permanently delete all past transaction records. This cannot be undone.'
                                : deleteDialog === 'cancelled'
                                ? 'This will permanently delete all cancelled orders. This cannot be undone.'
                                : deleteDialog === 'delivered'
                                ? 'This will permanently delete all delivered orders. This cannot be undone.'
                                : <>Permanently delete order <strong>{deleteDialog.orderId}</strong>? This cannot be undone.</>
                            }
                        </p>
                        <div className="apt-dialog-actions">
                            <button className="apt-btn apt-btn--secondary" onClick={() => setDeleteDialog(null)} disabled={actionLoading}>
                                Cancel
                            </button>
                            <button className="apt-btn apt-btn--danger" onClick={handleDelete} disabled={actionLoading}>
                                {actionLoading ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="apt-header">
                <div>
                    <h2 className="apt-title">Order Archive</h2>
                    <p className="apt-subtitle">{records.length} archived order{records.length !== 1 ? 's' : ''}</p>
                </div>
                {records.length > 0 && (
                    <button className="apt-delete-all-btn" onClick={() => setDeleteDialog('all')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                        </svg>
                        Delete All
                    </button>
                )}
            </div>

            {/* Content */}
            {records.length === 0 ? (
                <div className="apt-empty">No archived orders yet.</div>
            ) : (
                <div>
                    {/* CANCELLED ORDERS SECTION */}
                    {cancelledOrders.length > 0 && (
                        <div className="apt-section">
                            <div className="apt-section-header">
                                <h3 className="apt-section-title">Cancelled Orders ({cancelledOrders.length})</h3>
                                {cancelledOrders.length > 0 && (
                                    <button className="apt-delete-section-btn" onClick={() => setDeleteDialog('cancelled')}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                            <path d="M10 11v6M14 11v6"/>
                                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                        </svg>
                                        Delete All
                                    </button>
                                )}
                            </div>
                            <div className="apt-table-container">
                                <table className="apt-table">
                                    <thead>
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Customer</th>
                                            <th>Items</th>
                                            <th>Total</th>
                                            <th>Date Ordered</th>
                                            <th>Cancelled On</th>
                                            <th>Reason</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cancelledOrders.map(record => (
                                            <tr key={record.firestoreId}>
                                                <td className="apt-mono">{record.orderId || '—'}</td>
                                                <td>{record.customerName || '—'}</td>
                                                <td className="apt-center">{record.items || 0}</td>
                                                <td>₱ {(record.total || 0).toLocaleString()}</td>
                                                <td>{formatDate(record.date)}</td>
                                                <td>{formatDate(record.archivedAt)}</td>
                                                <td className="apt-reason">{record.cancellationReason || '—'}</td>
                                                <td>
                                                    <button
                                                        className="apt-delete-btn"
                                                        title="Delete permanently"
                                                        onClick={() => setDeleteDialog({ firestoreId: record.firestoreId, orderId: record.orderId })}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                                            <path d="M10 11v6M14 11v6"/>
                                                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* DELIVERED ORDERS SECTION */}
                    {deliveredOrders.length > 0 && (
                        <div className="apt-section">
                            <div className="apt-section-header">
                                <h3 className="apt-section-title">Delivered Orders ({deliveredOrders.length})</h3>
                                {deliveredOrders.length > 0 && (
                                    <button className="apt-delete-section-btn" onClick={() => setDeleteDialog('delivered')}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                            <path d="M10 11v6M14 11v6"/>
                                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                        </svg>
                                        Delete All
                                    </button>
                                )}
                            </div>
                            <div className="apt-table-container">
                                <table className="apt-table">
                                    <thead>
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Customer</th>
                                            <th>Items</th>
                                            <th>Total</th>
                                            <th>Date Ordered</th>
                                            <th>Delivered On</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveredOrders.map(record => (
                                            <tr key={record.firestoreId}>
                                                <td className="apt-mono">{record.orderId || '—'}</td>
                                                <td>{record.customerName || '—'}</td>
                                                <td className="apt-center">{record.items || 0}</td>
                                                <td>₱ {(record.total || 0).toLocaleString()}</td>
                                                <td>{formatDate(record.date)}</td>
                                                <td>{formatDate(record.archivedAt)}</td>
                                                <td><span className="apt-status apt-status--delivered">✓ Delivered</span></td>
                                                <td>
                                                    <button
                                                        className="apt-delete-btn"
                                                        title="Delete permanently"
                                                        onClick={() => setDeleteDialog({ firestoreId: record.firestoreId, orderId: record.orderId })}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                                            <path d="M10 11v6M14 11v6"/>
                                                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}